import React, { useState, useEffect } from 'react';

import { Modal, Divider, Table, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    LoadingOutlined,
    QuestionCircleOutlined,
    ReloadOutlined,
    CheckOutlined,
} from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import IBaseComponentProps from '@components/base-component-props';

import { exception, warning, securityRestriction } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import Counterparty from '@components/users/counterparty';

import { ICounterparty } from '@entities/counterparty';
import { ICounterpartyFilter } from '@entities/counterparty-filter';
import { IUserSession } from '@entities/user-session';

import { Permission, hasPermission } from '@enums/permission';

interface ICounterpartiesProps extends IBaseComponentProps {
    userId: string | undefined;
    isViewOnly: boolean;
}

const Counterparties = (props: ICounterpartiesProps) => {
    const { userId, isViewOnly, serverFetch } = props;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();

    const [counterparties, setCounterparties] = useState<Array<ICounterparty>>([]);
    const [selectedCounterpartyIds, setSelectedCounterpartyIds] = useState<React.Key[]>([]);
    const [currentCounterparty, setCurrentCounterparty] = useState<ICounterparty>();
    const [refreshRequired, setRefreshRequired] = useState<boolean>(!!userId);
    const [counterpartyOpen, setCounterpartyOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setCounterparties([]);

        setSelectedCounterpartyIds([]);
        setCurrentCounterparty(undefined);

        setRefreshRequired(false);

        setLoading(true);

        const counterpartyFilter: ICounterpartyFilter = {
            userId: userId,
        };

        serverFetch(`counterparties`, { method: 'GET', queryParams: counterpartyFilter })
            .then((data: any) => {
                setCounterparties(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка получения контрагентов', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshRequired]);

    const onDeleteCounterparty = () => {
        serverFetch(`counterparties/${currentCounterparty?.id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления контрагента', ex, () => d(userLoaded(undefined)));
            });
    };

    const onCounterpartySelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedCounterpartyIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = counterparties.find((c) => c.id === selectedRowKeys[0]);
            setCurrentCounterparty(entity);
        } else {
            setCurrentCounterparty(undefined);
        }
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loading || loading || !userId,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить ',
                        key: 'add',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        disabled: isViewOnly,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCounterparties)) {
                                securityRestriction(api, [Permission.ManageCounterparties]);
                                return;
                            }

                            setSelectedCounterpartyIds([]);
                            setCurrentCounterparty(undefined);

                            setCounterpartyOpen(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentCounterparty || isViewOnly,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCounterparties)) {
                                securityRestriction(api, [Permission.ManageCounterparties]);
                                return;
                            }

                            setCounterpartyOpen(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCounterparty || isViewOnly,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCounterparty)) {
                                securityRestriction(api, [Permission.DeleteCounterparty]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить контрагента "${currentCounterparty?.inn}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDeleteCounterparty();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ICounterparty> = [
            {
                title: 'Юр. имя',
                dataIndex: 'legalName',
                width: 350,
            },
            {
                title: 'ИНН',
                width: 180,
                dataIndex: 'inn',
            },
            {
                title: 'Основной',
                width: 90,
                align: 'center',
                render: (_: any, record) => {
                    return record.isMain ? <CheckOutlined /> : '';
                },
            },
            {
                title: 'Юр. адрес',
                dataIndex: 'legalAddress',
            },
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={counterparties}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                rowSelection={
                    isViewOnly
                        ? undefined
                        : {
                              selectedRowKeys: selectedCounterpartyIds,
                              onChange: onCounterpartySelectChange,
                              columnWidth: 32,
                              type: 'radio',
                          }
                }
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onCounterpartySelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 380px)`, x: 'max-content' }}
            />
        );
    };

    return (
        <>
            <Divider titlePlacement='left'>Контрагенты</Divider>

            {!isViewOnly && renderToolbar()}
            {renderTable()}

            {counterpartyOpen && (
                <Counterparty
                    counterparty={
                        currentCounterparty || {
                            id: undefined,
                            userId: userId,
                            legalName: undefined,
                            legalAddress: undefined,
                            inn: undefined,
                            isMain: counterparties.length <= 0,
                        }
                    }
                    isViewOnly={isViewOnly}
                    onClose={(e) => {
                        setRefreshRequired(e);
                        setCounterpartyOpen(false);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Counterparties;
